/**
 * Component is used to change product quantity on the cart page
 *
 * HTML Structure Example
 * <e2-quantity-select class="qty-select" .entry="${entry}" ?stock-reservation-enabled=${this.stockReservationEnabled}>
 * </e2-quantity-select>
 *
 * @prop {object} entry - current product entry in the cart
 * @prop {boolean} [stock-reservation-enabled] - the option to change available stock calculation logic.
 * When the stock reservation is enabled the Total Stock = Available Stock + Entry Quantity
 * otherwise Total Stock = Available Stock
 */

import { html } from 'lit-element';

import { SimpleLitElement } from '@elab/components';
import { templateToString } from '@elab/core/utils/template';
import { actions } from '@elab/core/store/cart';
import connect from '@elab/core/store/connect';
import { calculateMaxQuantity } from '@elab/core/utils/cart/CartUtils';

const PROPERTIES = {
    isOpen: { type: Boolean },
    placeholder: { type: String, attribute: 'placeholder' },
    cssModifier: { type: String, attribute: 'css-modifier' },
    options: { type: Array },
    customClasses: { type: String, attribute: 'custom-classes' },
    disabled: { type: Boolean },
    optionLabelPattern: { type: String, attribute: 'option-label-pattern' },
    disableOneElement: { type: Boolean, attribute: 'disable-one-element' },
};

class QuantitySelect extends SimpleLitElement {
    static get properties() {
        return {
            ...PROPERTIES,
            entry: { type: Object },
            stockReservationEnabled: {
                type: Boolean,
                attribute: 'stock-reservation-enabled',
            },
            fieldValue: { type: Object },
        };
    }

    constructor() {
        super();

        this.cssModifier = 'select--qty';
    }

    render() {
        const { cssModifier = '', disabled, isOpen } = this;
        const mod = [
            cssModifier,
            disabled ? 'select--disabled' : '',
            isOpen ? 'select--opened' : '',
        ].join(' ');

        const placeholderOpt = { name: null, label: this.placeholder };
        const values = (this.fieldValue && this.fieldValue.values) || [];
        const selectOptions = this.placeholder ? [placeholderOpt, ...values] : [...values];

        return html`
            <label class="select ${mod}" tabindex="0" @blur="${() => this.setStatus(false)}">
                <div class="select__wrapper" @click="${this.toggleStatus}">
                    <span class="select__selected-option"
                        >${this.getQuantity() || this.placeholder}</span
                    >
                    <span class="select__handler">
                        <i class="icon icon-kruidvat-arrow-down"></i>
                    </span>
                </div>

                <div class="select__options-container">
                    ${this.isOpen
                        ? html`
                              <e2-scroll
                                  class="select__items-container"
                                  wheel-propagation
                                  .active=${this.isOpen}
                              >
                                  <ul class="select__options">
                                      ${selectOptions.map(
                                          option => html`
                                              <li
                                                  class="select__option"
                                                  @click="${() => this.handleChange(option)}"
                                              >
                                                  ${templateToString(
                                                      option.label,
                                                      this.optionLabelPattern,
                                                  )}
                                              </li>
                                          `,
                                      )}
                                  </ul>
                              </e2-scroll>
                          `
                        : html``}
                </div>
            </label>
        `;
    }

    get fieldValue() {
        const maxQuantity = calculateMaxQuantity(this.entry, this.stockReservationEnabled);
        const values = [...Array(maxQuantity)].map((item, i) => ({
            label: (i + 1).toString(),
            value: i + 1,
        }));

        return { values };
    }

    handleChange(option) {
        this.dispatchEvent(
            new CustomEvent('handle-change', {
                detail: {
                    code: this.entry.product.code,
                    entryNumber: this.entry.entryNumber,
                    quantity: option.value,
                },
            }),
        );
        this.toggleStatus();
    }

    set fieldValue(newValue) {
        const oldValue = this.__fieldValue;
        this.__fieldValue = newValue;
        this.requestUpdate('fieldValue', oldValue);
    }

    setStatus(status) {
        this.isOpen = status;
    }

    toggleStatus() {
        this.setStatus(!this.isOpen);
    }

    getQuantity() {
        return this.entry.quantity || 0;
    }
}

const mapDispatchToEvents = dispatch => {
    return {
        'handle-change': e => dispatch(actions.updateCart({ ...e.detail, actionDelay: 0 })),
    };
};

export default () =>
    customElements.define('e2-quantity-select', connect(null, mapDispatchToEvents)(QuantitySelect));
